import { observable, action, computed, makeObservable } from 'mobx';
import { WelcomeStepFormType } from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/createAccount/signupWelcome/SignupWelcome.state';
import { ResponsibleGamblingState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/stepsStructure/responsibleGambling/ResponsibleGambling.state';
import { SignUpParamsType } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/SignUpParams';
import { CreateAccountStageType, CreateAccountStep } from './createAccount/CreateAccount.state';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { ExternalApi } from 'src/domains/layouts/state/externalApi/ExternalApi';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AccountState } from 'src/domains/players/shared/Types';

export type StepsForCirclesType =
    | 'create-account'
    | 'responsible-gambling'
    | 'deposit'
    | 'verification-failed'
    | 'gamstop-account-failed';

type SignUpMainStepsType =
    | {
          readonly type: 'create-account';
          readonly state: CreateAccountStep;
      }
    | {
          readonly type: 'responsible-gambling';
          readonly state: ResponsibleGamblingState;
      }
    | {
          readonly type: 'deposit';
      }
    | {
          readonly type: 'verification-failed';
          readonly accountId: number | null;
      }
    | {
          readonly type: 'gamstop-account-failed';
      };

export class SignUpMainSteps {
    @observable.ref public step: SignUpMainStepsType;
    @observable public stepsWithError: Array<StepsForCirclesType> = [];
    @observable.ref private createAccountStep: CreateAccountStep;
    //private createAccountStep: CreateAccountStep;

    public constructor(
        public readonly params: SignUpParamsType,
        private readonly setLoading: (isLoading: boolean) => void
    ) {
        makeObservable(this);

        const callbacks = {
            redirectToNextStep: this.redirectToResponsibleGambling,
            setLoading: this.setLoading,
            resetPromoCodeInLocalStorage: this.resetPromoCodeInLocalStorage,
        };

        this.createAccountStep = CreateAccountStep.newFromParams(params, callbacks);
        this.step = {
            type: 'create-account',
            state: this.createAccountStep,
        };
    }

    @action public redirectToStepThree = (): void => {
        this.redirectToDeposit();
    };

    @action public redirectToCreateAccount = (
        stage: CreateAccountStageType,
        initialUserData?: WelcomeStepFormType
    ): void => {
        if (initialUserData === undefined) {
            if (this.step.type === 'create-account') {
                this.step.state.currentStage = stage;
            }
            return;
        }

        const callbacks = {
            redirectToNextStep: this.redirectToResponsibleGambling,
            setLoading: this.setLoading,
            resetPromoCodeInLocalStorage: this.resetPromoCodeInLocalStorage,
        };

        this.createAccountStep = CreateAccountStep.newFromParams(this.params, callbacks, stage, initialUserData);

        this.step = {
            type: 'create-account',
            state: this.createAccountStep,
        };
    };

    @action public redirectToDeposit = (): void => {
        this.step = {
            type: 'deposit',
        };
    };

    @action public redirectToResponsibleGambling = (): void => {
        if (this.step.type === 'responsible-gambling') {
            console.warn('Ignore redirectToResponsibleGambling');
        } else {
            const callbacks = {
                redirectToVerificationFailed: this.redirectToVerificationFailed,
                redirectToGamStopAccountFailed: this.redirectToGamStopAccountFailed,
                redirectToNextStep: this.redirectToStepThree,
                setLoading: this.setLoading,
            };

            this.step = {
                type: 'responsible-gambling',
                state: new ResponsibleGamblingState(this.params, callbacks, this.createAccountStep),
            };
            const googleTagManager = GoogleTagManagerState.get(this.params.common);
            const accountState = AccountState.get(this.params.common);
            googleTagManager.gtmSignUpStepTwo(accountState.usersState);
            const config = ConfigComponents.get(this.params.common).config;
            if (config.sendInfoAboutSignUpToMobileApp === true) {
                ExternalApi.get(this.params.common).setSignupFinish();
            }
        }
    };

    @action public addStepWithError = (step: StepsForCirclesType): void => {
        if (this.stepsWithError.includes(step) === false) {
            this.stepsWithError.push(step);
        }
    };

    @action public resetPromoCodeInLocalStorage = (): void => {
        LocalStorageState.get(this.params.common).promoCode.setValue(null);
    };

    @action public removeStepFromError = (step: StepsForCirclesType): void => {
        if (this.stepsWithError.includes(step)) {
            const indexOfStep = this.stepsWithError.indexOf(step);
            this.stepsWithError.slice(indexOfStep, 1);
        }
    };

    @action public onMoveBack = (): void => {
        if (this.step.type === 'create-account') {
            this.step.state.currentStage = 'welcome';
        }
    };

    @computed public get isStepWithBackOption(): boolean {
        if (this.step.type === 'create-account') {
            return true;
        }
        return false;
    }

    @computed public get isCloseOptionBlocked(): boolean {
        if (this.step.type === 'responsible-gambling') {
            return true;
        }
        return false;
    }

    // OLD METHODS -----------------------------------------------------------------------------------

    @action public redirectToVerificationFailed = (accountId: number | null): void => {
        const googleTagManager = GoogleTagManagerState.get(this.params.common);
        googleTagManager.gtmSignUpVerificationFailed();
        this.step = {
            type: 'verification-failed',
            accountId: accountId,
        };
    };

    @action public redirectToGamStopAccountFailed = (): void => {
        this.step = {
            type: 'gamstop-account-failed',
        };
    };
}
